// Matrix + rectangle helpers.
//
// Everything in this app is kept in *PDF user space* (origin bottom-left, y-up,
// page rotation NOT applied). That is the one space shared by all three layers:
//
//   - pdf.js operator lists report path coordinates in it
//   - pdf.js viewports convert to/from it (`convertToViewportPoint` /
//     `convertToPdfPoint`), which is how we map to screen pixels for editing
//   - pdf-lib widget `/Rect` values are written in it
//
// Converting only at the edges keeps rotated pages and offset CropBoxes from
// leaking coordinate bugs into the detector.

export const IDENTITY = [1, 0, 0, 1, 0, 0];

/** Compose two matrices the way pdf.js does: point * m2 * m1. */
export function transform(m1, m2) {
  return [
    m1[0] * m2[0] + m1[2] * m2[1],
    m1[1] * m2[0] + m1[3] * m2[1],
    m1[0] * m2[2] + m1[2] * m2[3],
    m1[1] * m2[2] + m1[3] * m2[3],
    m1[0] * m2[4] + m1[2] * m2[5] + m1[4],
    m1[1] * m2[4] + m1[3] * m2[5] + m1[5],
  ];
}

export function applyTransform(x, y, m) {
  return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
}

/**
 * Transform an axis-aligned box by `m` and return the axis-aligned bounds of
 * the result. All four corners are mapped because `m` may rotate or skew.
 */
export function transformBox(box, m) {
  const corners = [
    applyTransform(box.x0, box.y0, m),
    applyTransform(box.x1, box.y0, m),
    applyTransform(box.x1, box.y1, m),
    applyTransform(box.x0, box.y1, m),
  ];
  const xs = corners.map((c) => c[0]);
  const ys = corners.map((c) => c[1]);
  return {
    x0: Math.min(...xs),
    y0: Math.min(...ys),
    x1: Math.max(...xs),
    y1: Math.max(...ys),
  };
}

/** Approximate scale factor of a matrix, used to convert local line widths. */
export function matrixScale(m) {
  return Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2])) || 1;
}

export const boxWidth = (b) => b.x1 - b.x0;
export const boxHeight = (b) => b.y1 - b.y0;

export function boxToRect(b) {
  return { x: b.x0, y: b.y0, width: b.x1 - b.x0, height: b.y1 - b.y0 };
}

export function rectToBox(r) {
  return { x0: r.x, y0: r.y, x1: r.x + r.width, y1: r.y + r.height };
}

export function intersectionOverUnion(a, b) {
  const ix = Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0);
  const iy = Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0);
  if (ix <= 0 || iy <= 0) return 0;
  const inter = ix * iy;
  const areaA = boxWidth(a) * boxHeight(a);
  const areaB = boxWidth(b) * boxHeight(b);
  const union = areaA + areaB - inter;
  return union > 0 ? inter / union : 0;
}

export function boxesOverlap(a, b, pad = 0) {
  return (
    a.x0 - pad < b.x1 && a.x1 + pad > b.x0 && a.y0 - pad < b.y1 && a.y1 + pad > b.y0
  );
}

/** Clamp a rect so it stays inside the page box. */
export function clampToPage(rect, pageBox) {
  const x = Math.max(pageBox.x0, Math.min(rect.x, pageBox.x1 - 1));
  const y = Math.max(pageBox.y0, Math.min(rect.y, pageBox.y1 - 1));
  return {
    x,
    y,
    width: Math.max(1, Math.min(rect.width, pageBox.x1 - x)),
    height: Math.max(1, Math.min(rect.height, pageBox.y1 - y)),
  };
}
